import { Grid, MenuItem, Select, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormEvent, useState } from 'react';
import {
  getClassFromFirstYear,
  getCurrentAcademicYear,
} from '../../helpers/academic-years';
import { Gender, Student } from '../../types/student';
import ClassSelect from '../ClassSelect';

interface StudentEditDialogProps {
  onClose: () => void;
  onConfirm: (newStudent: Student) => void;
  student: Student | null;
}

const StudentEditDialog: React.FC<StudentEditDialogProps> = ({
  onClose,
  onConfirm,
  student,
}) => {
  const [classNumber, setClassNumber] = useState(
    student?.firstYear ? getClassFromFirstYear(student.firstYear) : 1
  );

  const getGenderName = (gender: Gender) => {
    switch (gender) {
      case Gender.Male:
        return 'Chlapec';
      case Gender.Female:
        return 'Dívka';
      default:
        throw new Error(`Unsupported gender: ${gender}`);
    }
  };

  const handleClassChange = (newClass: number) => setClassNumber(newClass);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const updatedStudent: Student = {
      name: (formData.get('name') as string)?.trim(),
      gender: Number(formData.get('gender')) as Gender,
      firstYear: getCurrentAcademicYear() - (classNumber - 1),
    };

    if (student) {
      if (
        student.name === updatedStudent.name &&
        student.gender === updatedStudent.gender &&
        student.firstYear === updatedStudent.firstYear
      ) {
        onClose();
        return;
      }
      updatedStudent.id = student.id;
    }

    onConfirm(updatedStudent);
  };

  return (
    <Dialog
      open
      fullWidth
      disableRestoreFocus
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>{`${
        student ? 'Upravit' : 'Vytvořit nového'
      } žáka`}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          name="name"
          label="Celé jméno"
          type="text"
          fullWidth
          variant="standard"
          sx={{ mb: 2 }}
          inputProps={{
            pattern: '\\s*\\S+.*',
            title: 'Jméno nesmí být prázdné',
          }}
          defaultValue={student?.name}
        />
        <Grid container justifyContent="space-between">
          <Grid item xs={5}>
            <Select
              required
              fullWidth
              id="gender"
              name="gender"
              variant="standard"
              defaultValue={student?.gender.valueOf() ?? Gender.Male}
            >
              {Object.values(Gender)
                .filter((value): value is Gender => typeof value === 'number')
                .map((gender, index) => (
                  <MenuItem key={gender} value={index}>
                    {getGenderName(gender)}
                  </MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <ClassSelect
              onChange={handleClassChange}
              defaultValue={classNumber}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zrušit</Button>
        <Button variant="contained" type="submit">
          {student ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentEditDialog;
